import { Signal } from "../core/Signal.js";
import { event } from "../util/EventUtil.js";
import { KeyCode } from "../util/KeyCode.js";
import { object } from "../util/ObjectUtil.js";
import { TabTrapPushOptions, tabTrap } from "../util/TabTrap.js";

export interface ModalOptions {
    /**
     * The content of the modal.
     */
    content: string | HTMLElement | ((info: Modal) => string | HTMLElement);
    /**
     * The title of the modal.
     */
    title?: string;
    /**
     * When closable is false, you need a close button in your content.
     * That button should have the attribute data-modal-action="close".
     */
    closable?: boolean;
    tabTrap?: Partial<TabTrapPushOptions>;
    layout?: "full" | "center";
    /**
     * To add multiple CSS classes, separate them with a space.
     */
    className?: string;
}

class Modal {

    public readonly onDispose = Signal.create<[Modal]>();
    public readonly onClose = Signal.create<[Modal]>();
    public readonly onHide = Signal.create<[Modal]>();
    public readonly onShow = Signal.create<[Modal]>();
    public readonly onRemove = Signal.create<[Modal]>();

    public readonly element: HTMLElement;
    public readonly options: ModalOptions;

    private _content: HTMLElement;
    private _disposables: Disposable[] = [];
    private _removeTimeout: any = null;

    constructor(options: ModalOptions) {
        options = object.merge(
            {
                closable: true,
                layout: "center",
                title: "Hello World"
            }
            , options);

        this.options = options;

        this.element = document.createElement("div");
        this.element.className = "e5-modal e5-modal--closed e5-modal--layout-" + options.layout;
        if (options.className) {
            this.element.classList.add(...options.className.split(" "));
        }
        this.element.setAttribute("role", "dialog");
        this.element.setAttribute("aria-modal", "true");
        this.element.innerHTML = `
        <div class="e5-modal__background"></div>
        <div class="e5-modal__box">
            <div class="e5-modal__toolbar">
                <div class="e5-modal__toolbar-title"><span></span></div>
                <div class="e5-modal__toolbar-fill"></div>
                <button class="e5-modal__toolbar-button" data-modal-action="close">
                    <i class="fal fa-times"></i>
                </button>
            </div>
            <div class="e5-modal__content"></div>
        </div>
        `;

        this._content = this.element.querySelector(".e5-modal__content");

        this.closable = options.closable;
        if (options.title) {
            this.title = options.title;
        }

        let content: string | HTMLElement = null;
        if (typeof options.content === "function") {
            content = options.content(this);
        } else {
            content = options.content;
        }
        if (typeof content === "string") {
            this._content.innerHTML = content;
        }
        else {
            this._content.innerHTML = "";
            this._content.appendChild(content);
        }

        let keyRepeat = false;
        let keyRepeatTime = 0;

        this._disposables.push(
            event.on({
                scope: this.element,
                type: "click",
                selector: `[data-modal-action="close"]`,
                listener: (e) => {
                    if (!this.visible) {
                        return;
                    }

                    /**
                     * Prevent holding key down triggers multiple 
                     * times a click event. When the modal is opened by keyboard (enter, space)
                     * and the user holds the key down, the modal will close immediately when a 
                     * close button is focused.
                     */
                    if (keyRepeat
                        && Date.now() - keyRepeatTime < 400) {
                        return;
                    }

                    this.close();
                }
            })
        );

        this._disposables.push(
            event.on({
                scope: document,
                type: "keydown",
                listener: (e) => {

                    if (KeyCode.is(e.event, KeyCode.ENTER, KeyCode.SPACE)) {
                        keyRepeat = e.event.repeat;
                        if (keyRepeat) {
                            keyRepeatTime = Date.now();
                        }
                    }

                    if (!this.visible) {
                        return;
                    }

                    if (e.event.repeat) {
                        return;
                    }

                    if (KeyCode.is(e.event, KeyCode.ESCAPE)) {
                        if (this.options.closable) {
                            this.close();
                        }
                    }
                },
            })
        );
    }

    public close(): void {
        this.onClose.dispatch(this);
    }

    public get title(): string {
        return this.element.querySelector(".e5-modal__toolbar-title>span").textContent;
    }

    public set title(value: string) {
        this.element.querySelector(".e5-modal__toolbar-title>span").textContent = value;
    }

    public get closable(): boolean {
        return this.element.classList.contains("e5-modal--closable");
    }

    public set closable(value: boolean) {
        this.element.classList.toggle("e5-modal--closable", value);
        this.element.querySelector(".e5-modal__background").setAttribute("data-modal-action", value ? "close" : "none");
    }

    public get visible(): boolean {
        return !this.element.classList.contains("e5-modal--closed")
    }

    public set visible(value: boolean) {
        if (this.visible == value) {
            return;
        }
        this.element.classList.toggle("e5-modal--closed", !value);

        if (value) {
            document.body.appendChild(this.element);
        } else {
            if (this._removeTimeout) {
                clearTimeout(this._removeTimeout);
            }
            this._removeTimeout = setTimeout(() => {
                if (!this.visible
                    && this.element.parentNode) {
                    this.element.remove();
                    this.onRemove.dispatch(this);
                }
            }, 400);
        }

        if (value) {
            if (this.options.tabTrap) {
                tabTrap.push(
                    object.merge(this.options.tabTrap, {
                        element: this.element
                    })
                );
            } else {
                tabTrap.push(
                    {
                        element: this.element
                    }
                );
            }

        } else {
            tabTrap.pop();
        }

        if (value) {
            this.onShow.dispatch(this);
        } else {
            this.onHide.dispatch(this);
        }
    }

    public dispose(): void {
        this.onDispose.dispatch(this);

        let m = this as Mutable<this>;
        m.element.remove();
        m.element = null;
        m.options = null;
        m.onRemove.dispose();
        m.onRemove = null;
        m.onHide.dispose();
        m.onHide = null;
        m.onDispose.dispose();
        m.onDispose = null;
        m.onClose.dispose();
        m.onClose = null;
        m.onShow.dispose();
        m.onShow = null;
        this._content = null;

        if (this._removeTimeout) {
            clearTimeout(this._removeTimeout);
            this._removeTimeout = null;
        }

        for (let d of this._disposables) {
            d.dispose();
        }
        this._disposables = null;
    }
}

class ModalManager {
    private _stack: Modal[] = [];
    private _current: Modal = null;

    public show(options: ModalOptions): Modal {
        let modal = new Modal(options);
        modal.onClose.add(() => {

            const index = this._stack.indexOf(modal);

            if (this.current === modal) {
                this.current = this._stack[index - 1] || null;
            }

            this._stack.splice(index, 1);

            setTimeout(() => {
                modal.dispose();
                modal = null;
            }, 400);
        });

        this._stack.push(modal);

        this.current = modal;

        return modal;
    }

    public clearStack(): void {
        this._stack.forEach(m => m.close());
    }

    private get current(): Modal {
        return this._current;
    }

    private set current(value: Modal) {
        if (this._current == value) {
            return;
        }
        if (this._current) {
            this._current.visible = false;
        }
        this._current = value;
        if (this._current) {
            this._current.visible = true;
            document.documentElement.style.setProperty("--e5-modal__scrollbar-width",
                (window.innerWidth - document.documentElement.clientWidth) + "px");
            document.documentElement.classList.add("e5-modal__root--open");
        } else {
            setTimeout(() => {
                if (!this._current) {
                    document.documentElement.classList.remove("e5-modal__root--open");
                }
            }, 400);
        }
    }
}

export const modal = new ModalManager();